import { Location } from '@angular/common';
import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { FaqBuilder, InfoDropdown } from 'src/app/components/faq/faq.component';
import { FeatureType } from 'src/app/components/featured-plates/featured-plates.component';
import {
  BYOTermBreakdown,
  BYOTermResponse,
} from 'src/app/models/byoTermResponse';
import { Registration } from 'src/app/models/registration';
import { RegistrationService } from 'src/app/services/registration-service';
import {
  SearchReqResponse,
  SearchService,
} from 'src/app/services/search-service';
import {
  TrackingService,
  TRACKING_SEARCH_TYPE,
} from 'src/app/services/tracking-service';
import GlobalFaqs from "../../global-faqs";

@Component({
  selector: 'pla-byo-search-page',
  templateUrl: './byo-search-page.component.html',
  styleUrls: ['./byo-search-page.component.scss'],
})
export class BYOSearchPageComponent implements OnInit, OnDestroy {
  public styleBreakdowns: BYOTermResponse = null;
  private routeParamSub: Subscription = null;
  private searchId = 0;
  private routeParamFirstEmit = true;
  public raFeatured = new FeatureType(
    'Recently Added',
    'recently-added'
  );

  @Input() public style: string = 'current';
  @Input('breakdowns') public inputBreakdowns: BYOTermResponse = null; // FROM API ####TGE / ####TGE / TGE####
  @Input('searchDefault') public searchDefault: boolean = true;
  @Input('inlineSearch') public inlineSearch: boolean = false;
  @Input() public gotoResults: boolean = false;
  @Input() public showSwitch: boolean = true;

  public featuredNames: { name: string, plate: string, style: string, link: string }[] = [
    { name: 'JOHN', plate: 'JO11 N??', style: 'current', link: 'JO/11/N--' },
    { name: 'LOREN', plate: 'LO23 N??', style: 'current', link: 'LO/23/N--' },
    { name: 'CHIARA', plate: 'CH14 RA?', style: 'current', link: 'CH/14/RA-' },
    { name: 'LUKE', plate: '???1 UKE?', style: 'current', link: '--/-1/UKE' },
    { name: 'PETER', plate: 'PE73 R??', style: 'current', link: 'PE/73/R--' },
    { name: 'SARAH', plate: 'SA24 H??', style: 'current', link: 'SA/24/H--' },
    { name: 'STEVEN', plate: '?S73 VEN', style: 'current', link: 'ST/73/VEN' },
    { name: 'GEORGE', plate: 'GE02 GE?', style: 'current', link: 'GE/02/GE-' },
    { name: 'PAUL', plate: 'PA11 L??', style: 'current', link: 'PA/11/L--' },
    { name: 'KATIE', plate: 'KA71 E??', style: 'current', link: 'KA/71/E--' },
    { name: 'HUGO', plate: 'HU60 ???', style: 'current', link: 'HU/60/---' },
    { name: 'SIMON', plate: '??51 MON', style: 'current', link: '--/51/MON' },
  ]

  public faq: InfoDropdown[] = GlobalFaqs.GetFaqsByKey('Build Your Own');

  public results: SearchReqResponse;
  public resultChange: EventEmitter<SearchReqResponse> =
    new EventEmitter<SearchReqResponse>();

  @Output() public resultChangeEvent: EventEmitter<{ loading: boolean, results: SearchReqResponse }> = new EventEmitter<{ loading: boolean, results: SearchReqResponse }>();

  public searchTerm: string = '';
  public loading: boolean = false;
  public hasSearchCriteria: boolean = false;

  public no_character: string = '﹣';
  public any_character: string = '?';
  public wc_character: string = '*';

  constructor(
    private router: Router,
    private location: Location,
    private activatedRoute: ActivatedRoute,
    private searchService: SearchService,
    private registrationService: RegistrationService,
    private trackingService: TrackingService
  ) { }

  ngOnInit(): void {
    // inline = inside search page
    if (this.inlineSearch) {
      this.styleBreakdowns = this.inputBreakdowns;
      if (this.searchDefault) this.search();
      return;
    }

    this.styleBreakdowns = new BYOTermResponse().construct();
    this.applyRouteParams(this.activatedRoute.snapshot.paramMap);

    // When user navigates to a different BYO URL (e.g. link or back/forward), re-apply params and search
    this.routeParamSub = this.activatedRoute.paramMap.subscribe((paramMap) => {
      this.applyRouteParams(paramMap);
      if (!this.routeParamFirstEmit && this.hasSearchCriteria && this.searchDefault) this.search();
      this.routeParamFirstEmit = false;
    });

    if (!this.searchDefault) return;
    this.search();
  }

  ngOnDestroy(): void {
    if (this.routeParamSub) {
      this.routeParamSub.unsubscribe();
      this.routeParamSub = null;
    }
  }

  private applyRouteParams(paramMap: ParamMap): void {
    const style = paramMap.get('style');
    this.style = style == null ? 'current' : style;
    const prefix = paramMap.get('prefix') ?? '';
    const numbers = paramMap.get('numbers') ?? '';
    const letters = paramMap.get('letters') ?? '';
    this.hasSearchCriteria = !!(prefix || numbers || letters);
    if (this.hasSearchCriteria) {
      const _routeActive = this.styleBreakdowns[this.style] as BYOTermBreakdown;
      _routeActive.construct(this.style, prefix, numbers, letters);
      this.trackingService.TrackSearch(
        TRACKING_SEARCH_TYPE.BYO,
        `${prefix}${numbers}${letters}`,
        { style: this.style }
      );
    }
  }

  public tableHeaderSearchTerm(): string {
    return this.searchTerm.replace('$', this.wc_character).replace('$', this.wc_character).replace('$', this.wc_character);
  }

  public activeBreakdown(): BYOTermBreakdown {
    if (this.style == null) return new BYOTermBreakdown().construct('current', '', '', '');
    if (this.styleBreakdowns == null) return new BYOTermBreakdown().construct(this.style, '', '', '');
    var _ = this.styleBreakdowns[this.style] as BYOTermBreakdown;
    _.style = this.style; // force style
    return _;
  }

  public doInlineSearch(term: BYOTermBreakdown): void {
    // update model behind UI
    this.style = term.style;
    this.styleBreakdowns[this.style] = term;

    if (this.inlineSearch && !this.gotoResults) {
      this.search();
    } else {
      if (this.gotoResults) {
        this.router.navigate([`/byo-search/${this.style}/${term.prefix}/${term.numbers}/${term.letters}`])
      } else {
        // update route
        this.location.go(
          `/byo-search/${this.style}/${term.prefix}/${term.numbers}/${term.letters}`
        );
        // search without refreshing
        this.search();
      }
    }
  }

  private search(): void {
    if (
      this.activeBreakdown().prefix == '' &&
      this.activeBreakdown().numbers == '' &&
      this.activeBreakdown().letters == ''
    ) {
      this.hasSearchCriteria = false;
      return;
    }

    this.hasSearchCriteria = true;

    this.searchTerm = `${this.activeBreakdown().prefix}${this.activeBreakdown().numbers
      }${this.activeBreakdown().letters}`.replace(/-/gi, '?');

    const currentSearchId = ++this.searchId;
    this.loading = true;
    this.resultChangeEvent.emit({ loading: true, results: null });
    this.searchService.searchBYO(
      this.style,
      this.activeBreakdown().prefix,
      this.activeBreakdown().numbers,
      this.activeBreakdown().letters,
      (byoResults: Registration[]) => {
        if (currentSearchId !== this.searchId) return;
        this.loading = false;
        this.handleResults(byoResults || []);
      }
    );
  }

  public handleResults(registrations: Registration[]): void {
    registrations = this.registrationService.formatRegistrations(registrations);
    this.results = new SearchReqResponse('byo_id', registrations);
    this.resultChange.emit(this.results);
    this.resultChangeEvent.emit({ loading: false, results: this.results });
  }
}
